import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { IconSymbol } from 'components/icons/Icon.types';
import { maxUploadSizeAllowed } from 'features/admin/appSettingsSelectors';
import { IFolder, ILibrary } from 'features/folders/folderAPI';
import { findFolder, rebuildFolderTree } from 'features/folders/folderSlice';
import { IUser } from 'features/user/userAPI';
import {
  IFile,
  IFileCreate,
  deleteFile,
  deleteLargeText,
  patchFile,
  postFile,
} from './fileAPI';
// import { MimeTypes } from './mimetypes';

export enum AcceptedMimeType {
  // image = 'image/*',
  gif = 'image/gif',
  jpg = 'image/jpeg',
  png = 'image/png',
  svg = 'image/svg',
  doc = 'application/msword',
  docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls = 'application/vnd.ms-excel',
  xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt = 'application/vnd.ms-powerpoint',
  pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pdf = 'application/pdf',
  mp4 = 'video/mp4',
  webm = 'video/webm',
  unknown = '',
}

export type restrictedImageTypes = 'jpg' | 'png';
export type imageTypes = restrictedImageTypes | 'svg';
export type docTypes = 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx' | 'pdf';
export type summaryTypes = 'doc' | 'docx' | 'pdf';
export type videoTypes = 'mp4' | 'webm';
export type fileTypes = imageTypes | docTypes | videoTypes | 'url';

export type AcceptedFileType = {
  type: fileTypes;
  mime: AcceptedMimeType;
  ext: string[];
  size: number;
  icon: IconSymbol;
};

const jpg: AcceptedFileType = {
  type: 'jpg',
  mime: AcceptedMimeType.jpg,
  ext: ['.jpg', '.jpeg'],
  size: 3_000_000,
  icon: IconSymbol.mime_img,
};
const png: AcceptedFileType = {
  type: 'png',
  mime: AcceptedMimeType.png,
  ext: ['.png'],
  size: 3_000_000,
  icon: IconSymbol.mime_img,
};
const svg: AcceptedFileType = {
  type: 'svg',
  mime: AcceptedMimeType.svg,
  ext: ['.svg'],
  size: 3_000_000,
  icon: IconSymbol.mime_img,
};
const doc: AcceptedFileType = {
  type: 'doc',
  mime: AcceptedMimeType.doc,
  ext: ['.doc'],
  size: 10_000_000,
  icon: IconSymbol.mime_docx,
};
const docx: AcceptedFileType = {
  type: 'doc',
  mime: AcceptedMimeType.docx,
  ext: ['.docx'],
  size: 10_000_000,
  icon: IconSymbol.mime_docx,
};
const xls: AcceptedFileType = {
  type: 'xls',
  mime: AcceptedMimeType.xls,
  ext: ['.xls'],
  size: 10_000_000,
  icon: IconSymbol.mime_xlsx,
};
const xlsx: AcceptedFileType = {
  type: 'xls',
  mime: AcceptedMimeType.xlsx,
  ext: ['.xlsx'],
  size: 10_000_000,
  icon: IconSymbol.mime_xlsx,
};
const ppt: AcceptedFileType = {
  type: 'ppt',
  mime: AcceptedMimeType.ppt,
  ext: ['.ppt'],
  size: 20_000_000,
  icon: IconSymbol.mime_pptx,
};
const pptx: AcceptedFileType = {
  type: 'ppt',
  mime: AcceptedMimeType.pptx,
  ext: ['.pptx'],
  size: 20_000_000,
  icon: IconSymbol.mime_pptx,
};
const pdf: AcceptedFileType = {
  type: 'pdf',
  mime: AcceptedMimeType.pdf,
  ext: ['.pdf'],
  size: 20_000_000,
  icon: IconSymbol.mime_pdf,
};
const linkType: AcceptedFileType = {
  type: 'url',
  mime: AcceptedMimeType.unknown,
  ext: ['.???'],
  size: 0,
  // icon: IconSymbol.mime_unknown,
  icon: IconSymbol.link,
};
const mp4: AcceptedFileType = {
  type: 'mp4',
  mime: AcceptedMimeType.mp4,
  ext: ['.mp4'],
  size: 100_000_000,
  icon: IconSymbol.mime_video,
};
const webm: AcceptedFileType = {
  type: 'webm',
  mime: AcceptedMimeType.webm,
  ext: ['.webm'],
  size: 100_000_000,
  icon: IconSymbol.mime_video,
};

export function typesAllowed(types: AcceptedFileType[]): string {
  return types.flatMap((t) => t.ext).join(', ');
}
export function sizeAllowed(types: AcceptedFileType[], type: string): number {
  const cleanType = type.split('+')[0];
  const acc = types.find((t) => {
    const re = new RegExp(`^${t.mime.replace('*', '.*')}$`);
    return re.test(cleanType);
  });
  if (acc && acc.ext.length > 0) {
    // try to get max from app settings
    const s = maxUploadSizeAllowed(acc.type);
    if (s >= 0) return s;
    return acc.size; // use default
  }
  return 0;
}

export const restrictedImageFileTypes = [jpg, png];
export const imageFileTypes = [...restrictedImageFileTypes, svg];
export const docFileTypes = [doc, docx, xls, xlsx, ppt, pptx, pdf];
export const summaryFileTypes = [doc, docx, pdf];
export const videoFileTypes = [mp4, webm];
export const mediaFileTypes = [...imageFileTypes, ...videoFileTypes];
export const allFileTypes = [
  ...restrictedImageFileTypes,
  ...docFileTypes,
  ...videoFileTypes,
];

export function getImageTypeInfo(type: string): AcceptedFileType | null {
  const acc = mediaFileTypes.find((t) => {
    const re = new RegExp(`^${t.mime.replace('*', '.*')}$`);
    return re.test(type ?? '');
  });
  return acc ?? null;
}
export function isImageType(type: string): boolean {
  let isImg = type === 'img';
  if (isImg) return true;
  return imageFileTypes.some((t) => t.type === type || t.mime === type);
}
export function isVideoType(type: string): boolean {
  return videoFileTypes.some((t) => t.type === type || t.mime === type);
}
export function isSummaryType(type: string): boolean {
  return summaryFileTypes.some((t) => t.type === type || t.mime === type);
}
export function getFileTypeInfo(file: IFile | File | string): AcceptedFileType {
  let acc: AcceptedFileType | undefined;
  if (typeof file === 'string') {
    acc = allFileTypes.find((t) => {
      return file === t.type;
    });
  } else {
    const type = file instanceof File ? file.type : file.mimeType;
    acc = allFileTypes.find((t) => {
      const re = new RegExp(`^${t.mime.replace('*', '.*')}$`);
      return re.test(type ?? '');
    });
  }
  if (acc) return acc;
  if (typeof file === 'string' && isImageType(file))
    return restrictedImageFileTypes[0];
  return linkType;
}

export enum FolderLocation {
  Avatars,
  Images,
  Library,
}
export function getStorageFolderName(
  location: FolderLocation,
  folder?: string,
): string {
  let fname;
  switch (location) {
    case FolderLocation.Avatars:
      fname = `Avatars`;
      break;
    case FolderLocation.Images:
      fname = `Images`;
      break;
    case FolderLocation.Library:
      return `Library`;
  }
  if (!fname) fname = 'Uncategorised';
  return `${fname}/${folder ?? ''}`;
}

// Call from a reducer to find a file in the redux store
export function findFile(fileId: number, library: ILibrary): IFile | null {
  if (!fileId) return null;
  let file = library.allFolders
    ?.flatMap((fldr) => fldr.files)
    .find((f) => f?.id === fileId);
  if (!file)
    file = library.allFolders
      ?.flatMap((fldr) => fldr.files)
      .find((f) => f?.id === fileId);
  return file ?? null;
}

// Intended to be called from reducer only
function moveFile(
  data: IFile,
  fromFolderId: number | undefined,
  toFolderId: number | undefined,
  library: ILibrary,
) {
  console.log('moving file');
  const fromFolder = findFolder(fromFolderId, library);
  const toFolder = findFolder(toFolderId, library);
  if (toFolder && data.id) {
    const file = findFile(data.id, library);
    if (file) {
      Object.assign(file, data);
      // remove the file from where it came
      if (fromFolder?.files) {
        const fi = fromFolder.files.findIndex((f) => f.id === data.id);
        if (fi >= 0) fromFolder.files?.splice(fi, 1);
        // } else {
        //   // or from the old library files
        //   if (library.files) {
        //     const fi = library.files.findIndex((l) => l.id === data.id);
        //     if (fi >= 0) library.files.splice(fi, 1);
        //   }
      }
      // add the file to its destination
      toFolder.files?.push(file);
      rebuildFolderTree(library);
      return true;
    }
  }
  return false;
}

export const postFileAsync = createAsyncThunk(
  'file/postFile',
  async (
    {
      data,
      file,
      author,
    }: {
      data: IFileCreate;
      file: File;
      author: IUser;
    },
    thunkAPI,
  ) => {
    try {
      const res = await postFile(file, { ...data, uploadTime: new Date() });
      return { response: res };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchFileAsync = createAsyncThunk(
  'file/patchFile',
  async (
    {
      data,
      fromFolder,
    }: {
      data: IFile;
      fromFolder?: IFolder;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await patchFile(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchSummaryAsync = createAsyncThunk(
  'file/patchFile',
  async (
    {
      data,
    }: {
      data: IFile;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await patchFile(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteFileAsync = createAsyncThunk(
  'file/deleteFile',
  async (
    {
      file,
    }: {
      file: IFile;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deleteFile(file.id as number) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteLargeTextAsync = createAsyncThunk(
  'file/deleteLargeText',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      return {
        response: await deleteLargeText(id),
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

// export function addFileCases(builder: ActionReducerMapBuilder<IDialogueState>) {
export function addFileCases(builder: ActionReducerMapBuilder<any>) {
  builder
    .addCase(postFileAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postFileAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const folder = state.library.hasOwnProperty('allFolders')
        ? findFolder(action.meta.arg.data.folder, state.library)
        : null;
      const files = folder?.files ?? null;
      if (files) {
        const id = action.payload.response.data._id;
        const { storage, ...rest } = action.meta.arg.data;
        const data = {
          ...rest,
          fileName: action.meta.arg.file.name,
          author: action.meta.arg.author,
          id: id,
          uri: action.payload.response.data.message,
          folder: folder,
          sectionFiles: [],
        };
        files.push(data);
        rebuildFolderTree(state.library);
      } else
        console.log(
          "Oops, your file got lost, but no worries - we've saved already.",
        );
    })
    .addCase(postFileAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchFileAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchFileAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const fromFolderId = action.meta.arg.fromFolder?.id;
      if (fromFolderId !== undefined) {
        if (
          action.meta.arg.data.folder &&
          moveFile(
            action.meta.arg.data,
            fromFolderId,
            action.meta.arg.data.folder.id!,
            state.library,
          )
        )
          return;
      } else {
        const file = findFile(action.meta.arg.data.id as number, state.library);
        if (file) {
          Object.assign(file, action.meta.arg.data);
          rebuildFolderTree(state.library);
        }
        return;
      }
      console.log(
        "Oops, your update got lost, but no worries - we've saved already.",
      );
    })
    .addCase(patchFileAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteFileAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteFileAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const folder =
        state.library.hasOwnProperty('allFolders') &&
        action.meta.arg.file.folder
          ? findFolder(action.meta.arg.file.folder.id, state.library)
          : null;
      if (folder) {
        const files = folder?.files ?? null;
        if (
          files &&
          files.some((f: IFile) => f.id === action.meta.arg.file.id)
        ) {
          files.splice(
            files.findIndex((f: IFile) => f.id === action.meta.arg.file.id),
            1,
          );
          rebuildFolderTree(state.library);
          return;
        }
      } else {
        console.log(
          'Oops, your file got lost, but no worries - it has been deleted already.',
        );
      }
    })
    .addCase(deleteFileAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });

  // .addCase(asyncApiMessagesNew.fulfilled, (state, action) => {})
  // .addCase(asyncApiMessagesUpdated.fulfilled, (state, action) => {
  //   const payload = action.meta.arg.data;

  //   const stateObj = action.meta.arg.stateMan(state);
  //   const messages = stateObj.messages;

  //   const rootUpdated = payload.updated;

  //   if (deepSearchKey(rootUpdated, 'messages')) {
  //     const payloadMessages = rootUpdated.messages;

  //     const isLikes = deepSearchKeyType(
  //       payloadMessages,
  //       'likes',
  //       'array',
  //       true
  //     );

  //     if (!isLikes && deepSearchKey(payloadMessages, 'new')) {
  //       for (const newMessage of payloadMessages.new) {
  //         if (!arraySomeById(messages, newMessage.id)) {
  //           messages.push(newMessage);
  //         }
  //       }
  //     }

  //     if (deepSearchKey(payloadMessages, 'updated')) {
  //       for (const updatedMessage of payloadMessages.updated) {
  //         const messageIndex = findIndexById(messages, updatedMessage.id);

  //         if (isLikes) {
  //           const updatedLikes = updatedMessage.updated.likes;

  //           if (deepSearchKey(updatedLikes, 'new')) {
  //             for (const like of updatedLikes.new) {
  //               if (!arraySomeById(messages[messageIndex].likes, like.id)) {
  //                 messages[messageIndex].likes.push(like);
  //               }
  //             }
  //           }
  //           if (deepSearchKey(updatedLikes, 'removed')) {
  //             for (const like of updatedLikes.removed) {
  //               const likeIndex = findIndexById(
  //                 messages[messageIndex].likes,
  //                 like
  //               );

  //               if (likeIndex !== -1) {
  //                 messages[messageIndex].likes.splice(likeIndex, 1);
  //               }
  //             }
  //           }
  //         } else {
  //           messages[messageIndex] = updateObject(
  //             messages[messageIndex],
  //             updatedMessage.updated
  //           );
  //         }
  //       }
  //     }

  //     if (!isLikes && deepSearchKey(payloadMessages, 'removed')) {
  //       for (const deletedMessage of payloadMessages.removed) {
  //         const messageIndex = findIndexById(messages, deletedMessage);

  //         if (messageIndex !== -1) {
  //           messages.splice(messageIndex, 1);
  //         }
  //       }
  //     }
  //   }
  // })
  // .addCase(asyncApiMessagesRemoved.fulfilled, (state, action) => {});
}
